import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { RefreshCw, Play, Square, Sparkles, PenLine } from 'lucide-react'
import { Switch } from '@/components/ui/Switch'
import { TextArea } from '@/components/base/textarea/textarea'
import { Button } from '@/components/base/buttons'
import { LoadingSpinner } from '@/components/common'
import { useReloadGreeting, playGreetingAudio } from '@/hooks/api/useGreetings'
import { ToneChips } from './ToneChips'
import { cn } from '@/lib/utils'

interface GreetingSectionProps {
  assistantId?: number
  className?: string
}

// Helper to get config.greetings.dynamic_enabled
// Uses Boolean() to match v1 behavior: undefined/null/false → personalized mode
// For new assistants, useAssistantDefaults explicitly sets dynamic_enabled: true
function getIsDynamic(config: Record<string, unknown> | undefined): boolean {
  const greetings = config?.greetings as Record<string, unknown> | undefined
  return Boolean(greetings?.dynamic_enabled)
}

// Helper to get config.greetings.style
function getGreetingStyle(config: Record<string, unknown> | undefined): string {
  const greetings = config?.greetings as Record<string, unknown> | undefined
  return (greetings?.style as string) || 'simple_direct'
}

// Helper to get config.greetings.persona_name
function getPersonaName(config: Record<string, unknown> | undefined): string {
  const greetings = config?.greetings as Record<string, unknown> | undefined
  return (greetings?.persona_name as string) || 'Bonnie'
}

// Helper to get config.greetings.caller_language_detection
function getCallerLanguageDetection(config: Record<string, unknown> | undefined): boolean {
  const greetings = config?.greetings as Record<string, unknown> | undefined
  return Boolean(greetings?.caller_language_detection)
}

export function GreetingSection({ assistantId, className }: GreetingSectionProps) {
  const { t } = useTranslation()
  const { control, setValue, getValues } = useFormContext()

  // Watch relevant form values
  const config = useWatch({ control, name: 'config' }) as Record<string, unknown>
  const greeting = useWatch({ control, name: 'greeting' }) as string
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]
  const voiceId = useWatch({ control, name: 'voice_id' }) as number
  const assistantName = useWatch({ control, name: 'name' }) as string

  const isDynamic = getIsDynamic(config)
  const greetingStyle = getGreetingStyle(config)
  const personaName = getPersonaName(config)
  const callerLanguageDetection = getCallerLanguageDetection(config)

  // State for greeting preview
  const [previewText, setPreviewText] = useState<string>('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current = null
    }
    setIsPlaying(false)
  }, [])

  useEffect(() => () => cleanupAudio(), [cleanupAudio])

  // Mutation for reloading greeting
  const reloadGreeting = useReloadGreeting()

  // Load greeting preview when dynamic settings change
  useEffect(() => {
    if (!isDynamic) return

    const loadGreeting = async () => {
      try {
        const result = await reloadGreeting.mutateAsync({
          dynamic_enabled: true,
          style: greetingStyle,
          language: defaultLanguage,
          base_greeting: greeting,
          assistant_name: assistantName,
          persona_name: personaName,
          additional_languages: additionalLanguages,
        })

        const combined = result.ivr_language_message
          ? `${result.ivr_language_message}\n\n${result.greeting}`
          : result.greeting
        setPreviewText(combined)
      } catch (error) {
        console.error('Failed to load greeting preview:', error)
        setPreviewText('Hello, how can I help you today?')
      }
    }

    loadGreeting()
  }, [isDynamic, greetingStyle, defaultLanguage, additionalLanguages, personaName])

  // Toggle between dynamic and personalized
  const handleToggleDynamic = (isPersonalized: boolean) => {
    const currentConfig = getValues('config') || {}
    const currentGreetings = (currentConfig as Record<string, unknown>).greetings || {}

    setValue(
      'config',
      {
        ...currentConfig,
        greetings: {
          ...(currentGreetings as Record<string, unknown>),
          dynamic_enabled: !isPersonalized,
        },
      },
      { shouldDirty: true }
    )
  }

  // Handle style change
  const handleStyleChange = (style: string) => {
    const currentConfig = getValues('config') || {}
    const currentGreetings = (currentConfig as Record<string, unknown>).greetings || {}

    setValue(
      'config',
      {
        ...currentConfig,
        greetings: {
          ...(currentGreetings as Record<string, unknown>),
          style,
        },
      },
      { shouldDirty: true }
    )
  }

  // Handle persona name change
  const handlePersonaNameChange = (name: string) => {
    const currentConfig = getValues('config') || {}
    const currentGreetings = (currentConfig as Record<string, unknown>).greetings || {}

    setValue(
      'config',
      {
        ...currentConfig,
        greetings: {
          ...(currentGreetings as Record<string, unknown>),
          persona_name: name,
        },
      },
      { shouldDirty: true }
    )
  }

  // Handle caller language detection toggle
  const handleCallerLanguageDetectionChange = (enabled: boolean) => {
    const currentConfig = getValues('config') || {}
    const currentGreetings = (currentConfig as Record<string, unknown>).greetings || {}

    setValue(
      'config',
      {
        ...currentConfig,
        greetings: {
          ...(currentGreetings as Record<string, unknown>),
          caller_language_detection: enabled,
        },
      },
      { shouldDirty: true }
    )
  }

  // Refresh dynamic greeting
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      const result = await reloadGreeting.mutateAsync({
        dynamic_enabled: true,
        style: greetingStyle,
        language: defaultLanguage,
        base_greeting: greeting,
        assistant_name: assistantName,
        persona_name: personaName,
        additional_languages: additionalLanguages,
      })

      const combined = result.ivr_language_message
        ? `${result.ivr_language_message}\n\n${result.greeting}`
        : result.greeting
      setPreviewText(combined)
    } catch (error) {
      console.error('Failed to refresh greeting:', error)
    } finally {
      setTimeout(() => setIsRefreshing(false), 300)
    }
  }

  // Play greeting audio
  const handlePlayAudio = async () => {
    if (!voiceId) return

    if (isPlaying) {
      cleanupAudio()
      return
    }

    const textToPlay = isDynamic ? previewText : greeting

    try {
      setIsPlaying(true)
      const audio = await playGreetingAudio({
        text: textToPlay,
        language: defaultLanguage,
        id: assistantId || 0,
        voice_id: voiceId,
      })
      audioRef.current = audio
      audio.onended = () => {
        audioRef.current = null
        setIsPlaying(false)
      }
    } catch (error) {
      console.error('Failed to play audio:', error)
      setIsPlaying(false)
    }
  }

  // Strip <lang="..."> tags for visual display
  const displayLines = useMemo(() => {
    const plain = previewText.replace(/<lang="[^"]*">/g, '').replace(/<\/lang>/g, '')
    return plain.split('\n').filter((line) => line.trim().length > 0)
  }, [previewText])

  const canPlayAudio = !!voiceId && !!defaultLanguage

  return (
    <div className={cn('space-y-6', className)}>
      {/* Dynamic Greeting Toggle */}
      <div
        className={cn(
          'flex items-start justify-between gap-4 rounded-lg border p-4',
          isDynamic
            ? 'border-primary-200 bg-primary-50/50 dark:border-primary-900 dark:bg-primary-900/20'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 rounded-lg p-2',
              isDynamic ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-gray-100 dark:bg-gray-700'
            )}
          >
            <Sparkles className={cn('h-5 w-5', isDynamic ? 'text-primary-600' : 'text-gray-400')} />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.personality.dynamic_greeting.title')}
            </h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.personality.dynamic_greeting.description')}
            </p>
          </div>
        </div>
        <Switch isSelected={isDynamic} onChange={() => handleToggleDynamic(false)} />
      </div>

      {/* Dynamic Greeting Content */}
      {isDynamic && (
        <div className="space-y-4 pl-4">
          {/* Tone Chips */}
          <ToneChips value={greetingStyle} onChange={handleStyleChange} />

          {/* Caller Language Detection Toggle */}
          <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                {t('resources.assistants.personality.caller_language_detection.label')}
              </h4>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.personality.caller_language_detection.description')}
              </p>
            </div>
            <Switch
              isSelected={callerLanguageDetection}
              onChange={handleCallerLanguageDetectionChange}
            />
          </div>

          {/* Preview Box */}
          <div
            className={cn(
              'flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4',
              'dark:border-gray-700 dark:bg-gray-800',
              isRefreshing && 'opacity-60'
            )}
          >
            {/* Play button */}
            {canPlayAudio ? (
              <button
                type="button"
                onClick={handlePlayAudio}
                disabled={reloadGreeting.isPending}
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors',
                  'border-2 border-gray-300 bg-white text-gray-600',
                  'hover:border-primary-500 hover:text-primary-600',
                  'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300',
                  isPlaying && 'border-primary-500 text-primary-600'
                )}
              >
                {isPlaying ? <Square className="h-4 w-4 fill-current" /> : <Play className="ml-0.5 h-5 w-5" />}
              </button>
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-gray-200 bg-gray-100 text-gray-300 dark:border-gray-700 dark:bg-gray-800">
                <Play className="h-5 w-5" />
              </div>
            )}

            {/* Preview text */}
            <div className="flex flex-1 flex-col gap-1">
              {reloadGreeting.isPending && !previewText ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <LoadingSpinner className="h-4 w-4" />
                  {t('resources.assistants.personality.generating_greeting')}
                </div>
              ) : (
                displayLines.map((line, idx) => (
                  <p key={idx} className="text-sm italic text-gray-700 dark:text-gray-300">
                    {line}
                  </p>
                ))
              )}
            </div>

            {/* Refresh button */}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing || reloadGreeting.isPending}
              className={cn(
                'rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600',
                'dark:hover:bg-gray-700 dark:hover:text-gray-300',
                isRefreshing && 'animate-spin'
              )}
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Personalized Greeting Toggle */}
      <div
        className={cn(
          'flex items-start justify-between gap-4 rounded-lg border p-4',
          !isDynamic
            ? 'border-primary-200 bg-primary-50/50 dark:border-primary-900 dark:bg-primary-900/20'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 rounded-lg p-2',
              !isDynamic ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-gray-100 dark:bg-gray-700'
            )}
          >
            <PenLine className={cn('h-5 w-5', !isDynamic ? 'text-primary-600' : 'text-gray-400')} />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.personality.personalized_greeting.title')}
            </h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.personality.personalized_greeting.description')}
            </p>
          </div>
        </div>
        <Switch isSelected={!isDynamic} onChange={() => handleToggleDynamic(true)} />
      </div>

      {/* Personalized Greeting Content */}
      {!isDynamic && (
        <div className="space-y-4 pl-4">
          {/* Textarea */}
          <div>
            <TextArea
              value={greeting || ''}
              onChange={(value) => setValue('greeting', value, { shouldDirty: true })}
              placeholder={t('resources.assistants.personality.personalized_greeting.helper')}
              rows={4}
              maxLength={1000}
            />
            <p className="mt-1 text-right text-sm text-gray-500">
              {t('resources.assistants.personality.personalized_greeting.character_counter', {
                charCount: (greeting || '').length,
                maxChars: 1000,
              })}
            </p>
          </div>

          {/* Listen button */}
          <Button
            type="button"
            color="primary"
            onClick={handlePlayAudio}
            isDisabled={!canPlayAudio || !greeting}
            isLoading={isPlaying}
            iconLeading={isPlaying ? Square : Play}
          >
            {t('resources.assistants.personality.personalized_greeting.listen')}
          </Button>
        </div>
      )}

      {/* Advanced Settings */}
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
          <span className="transition-transform group-open:rotate-90">▶</span>
          {t('resources.assistants.personality.advanced_settings')}
        </summary>
        <div className="mt-4 space-y-4 pl-4">
          {/* Persona Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('resources.assistants.personality.persona_name')}
            </label>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.personality.persona_name_description')}
            </p>
            <input
              type="text"
              value={personaName}
              onChange={(e) => handlePersonaNameChange(e.target.value)}
              placeholder="Bonnie"
              className={cn(
                'mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm',
                'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
                'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
              )}
            />
          </div>
        </div>
      </details>
    </div>
  )
}

export default GreetingSection
